import SEOHead from "@/components/SEOHead";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Check } from "lucide-react";
import { motion } from "framer-motion";
import { useEffect } from "react";

// Initialize Cal.com
declare global {
  interface Window {
    Cal?: any;
  }
}

const initCal = () => {
  if (window.Cal) return;
  
  (function (C: any, A: string, L: string) {
    let p = function (a: any, ar: any) {
      a.q.push(ar);
    };
    let d = C.document;
    C.Cal =
      C.Cal ||
      function () {
        let cal = C.Cal;
        let ar = arguments;
        if (!cal.loaded) {
          cal.ns = {};
          cal.q = cal.q || [];
          d.head.appendChild(d.createElement("script")).src = A;
          cal.loaded = true;
        }
        if (ar[0] === L) {
          const api = function () {
            p(api, arguments);
          };
          const namespace = ar[1];
          api.q = api.q || [];
          if (typeof namespace === "string") {
            cal.ns[namespace] = cal.ns[namespace] || api;
            p(cal.ns[namespace], ar);
            p(cal, ["initNamespace", namespace]);
          } else p(cal, ar);
          return;
        }
        p(cal, ar);
      };
  })(window, "https://app.cal.com/embed/embed.js", "init");

  window.Cal("init", "premier-contact-accompagnement-strategique", { origin: "https://app.cal.com" });
  window.Cal.ns["premier-contact-accompagnement-strategique"]("ui", { "hideEventTypeDetails": false, "layout": "month_view" });
}

const plans = [
  {
    name: "Partenaire 10",
    subtitle: "Pour structurer votre marketing et bâtir une base solide",
    description: "Ce forfait est conçu pour les entreprises qui souhaitent mettre en place une présence marketing cohérente, professionnelle et stratégique, sans complexité inutile.",
    price: "3 000",
    period: "/mois",
    features: [
      "10 reels par mois",
      "1 demi-journée de tournage",
      "1 pub premium incluse",
      "Élaboration de la stratégie de contenu (angles, messages, formats)",
      "Optimisation des vidéos pour les plateformes choisies",
      "Publication sur les réseaux sociaux",
      "Rapport de performance mensuel",
    ],
    ideal: "Les entreprises qui veulent structurer leur marketing et assurer une présence constante et stratégique.",
    cta: "Démarrer une collaboration",
    popular: false,
  },
  {
    name: "Partenaire 20",
    subtitle: "Pour générer de la visibilité qualifiée et soutenir l'acquisition",
    description: "Ce forfait s'adresse aux entreprises qui veulent aller plus loin qu'une simple présence et commencer à utiliser le contenu comme levier de croissance.",
    price: "4 500",
    period: "/mois",
    features: [
      "20 reels par mois",
      "1 journée de tournage",
      "2 pubs premium incluses",
      "Stratégie de contenu avancée, orientée conversion",
      "Gestion et optimisation des publications",
      "Appel stratégique mensuel",
      "Rapports de performance détaillés",
    ],
    ideal: "Les entreprises en croissance qui veulent combiner contenu organique et publicité pour générer des opportunités.",
    cta: "Planifier un appel stratégique",
    popular: true,
  },
  {
    name: "Partenaire 30",
    subtitle: "Pour les entreprises qui veulent déléguer et accélérer leur croissance",
    description: "Ce forfait offre un accompagnement marketing complet pour les organisations qui souhaitent maximiser leur présence, amplifier leur portée et soutenir une croissance soutenue.",
    price: "6 800",
    period: "/mois",
    features: [
      "30 reels par mois",
      "2 journées de tournage",
      "3 pubs premium incluses",
      "Stratégie de contenu et d'acquisition complète",
      "Gestion des réseaux sociaux",
      "Optimisation des messages et des performances",
      "Suivi rapproché et ajustements continus",
      "Responsable de compte dédié",
    ],
    ideal: "Les entreprises établies qui veulent scaler leur marketing sans multiplier les fournisseurs.",
    cta: "Discuter de votre projet",
    popular: false,
  },
];

const Tarifs = () => {
  useEffect(() => {
    initCal();
  }, []);

  return (
    <>
      <SEOHead
        title="Forfaits marketing Montréal — Agence 8020 | Partenaire 10, 20, 30"
        description="Découvrez les forfaits marketing d'Agence 8020 pour entrepreneurs au Québec : Partenaire 10 (3 000$/mois), Partenaire 20 (4 500$/mois) et Partenaire 30 (6 800$/mois)."
        canonical="/tarifs"
      />
      <main className="min-h-screen bg-background">
        <Navbar />
      
      <section className="pt-32 pb-20 px-4">
        <div className="container mx-auto max-w-7xl">
          {/* Header */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <span className="inline-block px-4 py-2 rounded-full bg-primary/10 text-primary text-sm font-medium mb-6">
              Tarifs
            </span>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-heading font-bold mb-6">
              Des forfaits adaptés à{" "}
              <span className="text-primary">vos ambitions</span>
            </h1>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Choisissez le plan qui correspond à vos objectifs. 
              Tous nos forfaits incluent notre expertise et un accompagnement personnalisé.
            </p>
          </motion.div>

          {/* Pricing Cards */}
          <div className="grid lg:grid-cols-3 gap-8">
            {plans.map((plan, index) => (
              <motion.div
                key={plan.name}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className={`relative rounded-2xl p-8 flex flex-col ${
                  plan.popular
                    ? "bg-primary text-primary-foreground ring-4 ring-primary/20 lg:scale-105"
                    : "bg-card border border-border"
                }`}
              >
                {plan.popular && (
                  <div className="absolute -top-4 left-1/2 -translate-x-1/2 px-4 py-1 bg-background text-primary text-sm font-medium rounded-full border border-primary">
                    Le plus populaire
                  </div>
                )}

                <div className="mb-6">
                  <h3 className="text-2xl font-heading font-bold mb-2">
                    {plan.name}
                  </h3>
                  <p className={`text-sm font-medium mb-3 ${plan.popular ? "text-primary-foreground" : "text-primary"}`}>
                    {plan.subtitle}
                  </p>
                  <p className={`text-sm ${plan.popular ? "text-primary-foreground/80" : "text-muted-foreground"}`}>
                    {plan.description}
                  </p>
                </div>

                <div className="mb-8">
                  <span className="text-4xl font-bold">{plan.price}$</span>
                  <span className={`${plan.popular ? "text-primary-foreground/80" : "text-muted-foreground"}`}>
                    {plan.period}
                  </span>
                </div>

                <ul className="space-y-3 mb-6 flex-grow">
                  {plan.features.map((feature) => (
                    <li key={feature} className="flex items-start gap-3">
                      <Check className={`w-5 h-5 mt-0.5 flex-shrink-0 ${
                        plan.popular ? "text-primary-foreground" : "text-primary"
                      }`} />
                      <span className={`text-sm ${plan.popular ? "text-primary-foreground/90" : "text-foreground"}`}>
                        {feature}
                      </span>
                    </li>
                  ))}
                </ul>

                <div className={`p-4 rounded-xl mb-6 ${plan.popular ? "bg-primary-foreground/10" : "bg-muted"}`}>
                  <p className={`text-sm font-medium mb-1 ${plan.popular ? "text-primary-foreground" : "text-foreground"}`}>
                    Idéal pour :
                  </p>
                  <p className={`text-sm ${plan.popular ? "text-primary-foreground/80" : "text-muted-foreground"}`}>
                    {plan.ideal}
                  </p>
                </div>

                <Button
                  data-cal-link="maxim-rousseau-6ciolz/premier-contact-accompagnement-strategique"
                  data-cal-namespace="premier-contact-accompagnement-strategique"
                  data-cal-config='{"layout":"month_view"}'
                  className={`w-full ${
                    plan.popular
                      ? "bg-background text-primary hover:bg-background/90"
                      : "bg-primary text-primary-foreground hover:bg-primary/90"
                  }`}
                  size="lg"
                >
                  {plan.cta}
                </Button>
              </motion.div>
            ))}
          </div>

          {/* Modalités */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="mt-20 p-8 rounded-2xl bg-card border border-border"
          >
            <h3 className="text-2xl font-heading font-bold mb-6 text-center">
              Modalités de collaboration
            </h3>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="text-center p-4">
                <p className="text-muted-foreground text-sm">Prix de départ</p>
                <p className="text-xl font-bold text-foreground">3 000$ / mois</p>
              </div>
              <div className="text-center p-4">
                <p className="text-muted-foreground text-sm">Engagement minimum</p>
                <p className="text-xl font-bold text-foreground">3 mois</p>
              </div>
              <div className="text-center p-4">
                <p className="text-muted-foreground text-sm">Budget publicitaire</p>
                <p className="text-xl font-bold text-foreground">Non inclus</p>
              </div>
              <div className="text-center p-4">
                <p className="text-muted-foreground text-sm">Accompagnement</p>
                <p className="text-xl font-bold text-foreground">Personnalisé</p>
              </div>
            </div>
          </motion.div>

          {/* Notre approche */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.5 }}
            className="text-center mt-16 p-8 rounded-2xl bg-muted/50 border border-border"
          >
            <h3 className="text-2xl font-heading font-bold mb-4">
              Notre approche
            </h3>
            <p className="text-muted-foreground mb-2 max-w-2xl mx-auto">
              Agence 80/20 ne vend pas simplement des vidéos.
            </p>
            <p className="text-foreground font-medium max-w-2xl mx-auto mb-8">
              Nous utilisons le contenu et la publicité comme des outils stratégiques pour soutenir la croissance des entreprises que nous accompagnons.
            </p>
            <Button 
              variant="outline" 
              size="lg"
              data-cal-link="maxim-rousseau-6ciolz/premier-contact-accompagnement-strategique"
              data-cal-namespace="premier-contact-accompagnement-strategique"
              data-cal-config='{"layout":"month_view"}'
            >
              Discuter de mon projet
            </Button>
          </motion.div>
        </div>
      </section>

      <Footer />
      </main>
    </>
  );
};

export default Tarifs;